import { z } from "zod";
import {
  CONTINENTS,
  DIFFICULTY_LEVELS,
  TRIP_TYPES,
  TRIP_STATUSES,
  TRIP_DATE_STATUSES,
  ORDER_STATUSES,
  USER_ROLES,
  COMMUNICATION_TYPES,
  COMMUNICATION_DIRECTIONS,
} from "../types";

// ============================================================================
// COMMON SCHEMAS
// ============================================================================

export const paginationSchema = z.object({
  page: z.coerce.number().min(1).default(1),
  limit: z.coerce.number().min(1).max(100).default(20),
});

export const idParamSchema = z.object({
  id: z.string().min(1),
});

export const slugParamSchema = z.object({
  slug: z.string().min(1),
});

// ============================================================================
// DESTINATION SCHEMAS
// ============================================================================

export const highlightSchema = z.object({
  title: z.string(),
  description: z.string(),
  image: z.string().optional(),
});

export const regionSchema = z.object({
  name: z.string(),
  description: z.string(),
  image: z.string().optional(),
});

export const practicalInfoSchema = z.object({
  visa: z.string().optional(),
  currency: z.string().optional(),
  language: z.string().optional(),
  timezone: z.string().optional(),
  electricity: z.string().optional(),
  vaccination: z.string().optional(),
});

export const destinationCreateSchema = z.object({
  slug: z.string().min(1).max(100),
  name: z.string().min(1).max(200),
  nameSk: z.string().max(200).optional(),
  continent: z.enum(CONTINENTS),

  // Descriptions
  shortDescription: z.string().optional(),
  shortDescriptionSk: z.string().optional(),
  description: z.string().optional(),
  descriptionSk: z.string().optional(),

  // Travel info
  bestTimeToVisit: z.string().max(500).optional(),
  climateInfo: z.string().optional(),

  // Practical info
  practicalInfo: practicalInfoSchema.optional(),

  // Rich content
  highlights: z.array(highlightSchema).optional(),
  regions: z.array(regionSchema).optional(),
  cuisine: z.string().optional(),
  cuisineSk: z.string().optional(),
  activities: z.array(z.string()).optional(),
  whyWithUs: z.array(z.string()).optional(),

  // Media
  featuredImage: z.string().optional(),
  galleryImages: z.array(z.string()).optional(),
  heroVideoUrl: z.string().optional(),

  // Relations
  parentId: z.string().optional(),
  guideId: z.string().optional(),

  // SEO
  metaTitle: z.string().max(60).optional(),
  metaDescription: z.string().max(160).optional(),

  // Settings
  sortOrder: z.number().int().default(0),
  isFeatured: z.boolean().default(false),
});

export const destinationUpdateSchema = destinationCreateSchema.partial();

// ============================================================================
// GUIDE SCHEMAS
// ============================================================================

export const guideCreateSchema = z.object({
  slug: z.string().min(1).max(100),
  name: z.string().min(1).max(200),
  photo: z.string().url().optional(),
  bio: z.string().optional(),
  bioSk: z.string().optional(),
  livingIn: z.string().max(200).optional(),
  yearsExperience: z.number().int().min(0).optional(),
  specializations: z.array(z.string()).optional(),
  youtubeChannelUrl: z.string().url().optional(),
  featuredVideos: z.array(z.string().url()).optional(),
  email: z.string().email().optional(),
  phone: z.string().max(30).optional(),
  whatsapp: z.string().max(30).optional(),
  isActive: z.boolean().default(true),
  sortOrder: z.number().int().default(0),
});

export const guideUpdateSchema = guideCreateSchema.partial();

// ============================================================================
// TRIP SCHEMAS
// ============================================================================

export const itineraryDaySchema = z.object({
  day: z.number().int().min(1),
  title: z.string().min(1).max(200),
  description: z.string().optional(),
  image: z.string().url().optional(),
  accommodation: z.string().max(200).optional(),
  meals: z
    .object({
      breakfast: z.boolean(),
      lunch: z.boolean(),
      dinner: z.boolean(),
    })
    .optional(),
});

export const tripCreateSchema = z.object({
  slug: z.string().min(1).max(100),
  title: z.string().min(1).max(300),
  titleSk: z.string().max(300).optional(),
  shortDescription: z.string().max(500).optional(),
  shortDescriptionSk: z.string().max(500).optional(),
  description: z.string().optional(),
  descriptionSk: z.string().optional(),
  priceFrom: z.number().int().min(0),
  priceOriginal: z.number().int().min(0).optional(),
  priceEur: z.number().int().min(0).optional(),
  durationDays: z.number().int().min(1),
  difficultyLevel: z.enum(DIFFICULTY_LEVELS).optional(),
  groupSizeMin: z.number().int().min(1).default(2),
  groupSizeMax: z.number().int().min(1).default(16),
  tripType: z.enum(TRIP_TYPES),
  guideId: z.string().optional(),
  destinationId: z.string().optional(),
  featuredImage: z.string().url().optional(),
  galleryImages: z.array(z.string().url()).optional(),
  youtubeVideos: z.array(z.string().url()).optional(),
  guideVideoUrl: z.string().url().optional().or(z.literal("")),
  itinerary: z.array(itineraryDaySchema).optional(),
  included: z.array(z.string()).optional(),
  excluded: z.array(z.string()).optional(),
  isFeatured: z.boolean().default(false),
  isLastMinute: z.boolean().default(false),
  hasCzechGuide: z.boolean().default(true),
  allowsOnlineBooking: z.boolean().default(false),
  tags: z.array(z.string()).optional(),
  metaTitle: z.string().max(60).optional(),
  metaDescription: z.string().max(160).optional(),
  status: z.enum(TRIP_STATUSES).default("draft"),
});

export const tripUpdateSchema = tripCreateSchema.partial();

export const tripFilterSchema = z.object({
  destination: z.string().optional(),
  continent: z.enum(CONTINENTS).optional(),
  tripType: z.enum(TRIP_TYPES).optional(),
  difficultyLevel: z.enum(DIFFICULTY_LEVELS).optional(),
  priceMin: z.coerce.number().min(0).optional(),
  priceMax: z.coerce.number().min(0).optional(),
  durationMin: z.coerce.number().min(1).optional(),
  durationMax: z.coerce.number().min(1).optional(),
  hasCzechGuide: z.coerce.boolean().optional(),
  isFeatured: z.coerce.boolean().optional(),
  isLastMinute: z.coerce.boolean().optional(),
  tags: z.string().optional(), // comma-separated
  search: z.string().optional(),
  sortBy: z
    .enum(["price", "duration", "date", "popularity"])
    .default("popularity"),
  sortOrder: z.enum(["asc", "desc"]).default("asc"),
  ...paginationSchema.shape,
});

// ============================================================================
// TRIP DATE SCHEMAS
// ============================================================================

export const tripDateCreateSchema = z.object({
  tripId: z.string().min(1),
  startDate: z.coerce.date(),
  endDate: z.coerce.date(),
  price: z.number().int().min(0),
  priceEur: z.number().int().min(0).optional(),
  priceSingleRoom: z.number().int().min(0).optional(),
  capacity: z.number().int().min(1),
  bookedCount: z.number().int().min(0).default(0),
  guideId: z.string().optional(),
  status: z.enum(TRIP_DATE_STATUSES).default("open"),
  notes: z.string().optional(),
});

export const tripDateUpdateSchema = tripDateCreateSchema.partial();

// ============================================================================
// INQUIRY SCHEMA (Public form)
// ============================================================================

export const inquirySchema = z
  .object({
    tripId: z.string().min(1),
    tripDateId: z.string().optional(),
    firstName: z.string().min(1, "Jméno je povinné").max(100),
    lastName: z.string().min(1, "Příjmení je povinné").max(100),
    email: z.string().email("Neplatný email"),
    phone: z.string().max(20).optional(),
    adults: z.number().int().min(1).max(20).default(2),
    children: z.number().int().min(0).max(10).default(0),
    notes: z.string().max(2000).optional(),
    utmSource: z.string().optional(),
    utmMedium: z.string().optional(),
    utmCampaign: z.string().optional(),
  })
  // Transform to internal field names for API compatibility
  .transform((data) => ({
    ...data,
    adultsCount: data.adults,
    childrenCount: data.children,
  }));

// ============================================================================
// CUSTOMER SCHEMAS
// ============================================================================

export const customerCreateSchema = z.object({
  email: z.string().email(),
  phone: z.string().max(30).optional(),
  firstName: z.string().max(100).optional(),
  lastName: z.string().max(100).optional(),
  street: z.string().max(200).optional(),
  city: z.string().max(100).optional(),
  zip: z.string().max(20).optional(),
  country: z.string().length(2).default("CZ"),
  birthDate: z.coerce.date().optional(),
  passportNumber: z.string().max(50).optional(),
  passportExpiry: z.coerce.date().optional(),
  nationality: z.string().max(50).optional(),
  newsletterSubscribed: z.boolean().default(false),
  newsletterPreferences: z.array(z.string()).optional(),
  notes: z.string().optional(),
  source: z.string().max(50).optional(),
});

export const customerUpdateSchema = customerCreateSchema.partial();

// ============================================================================
// ORDER SCHEMAS
// ============================================================================

export const participantSchema = z.object({
  name: z.string().min(1).max(200),
  birthDate: z.string().optional(),
  passport: z.string().max(50).optional(),
  passportExpiry: z.string().optional(),
  nationality: z.string().max(50).optional(),
});

export const orderCreateSchema = z.object({
  customerId: z.string().min(1),
  tripId: z.string().min(1),
  tripDateId: z.string().optional(),
  participants: z.array(participantSchema).min(1),
  adultsCount: z.number().int().min(1).default(2),
  childrenCount: z.number().int().min(0).default(0),
  totalPrice: z.number().int().min(0),
  currency: z.string().length(3).default("CZK"),
  depositAmount: z.number().int().min(0).optional(),
  depositDueDate: z.coerce.date().optional(),
  source: z.string().default("web_form"),
  customerNotes: z.string().optional(),
  internalNotes: z.string().optional(),
});

export const orderUpdateSchema = orderCreateSchema.partial();

export const orderStatusChangeSchema = z.object({
  status: z.enum(ORDER_STATUSES),
  note: z.string().optional(),
});

// ============================================================================
// NEWSLETTER SCHEMAS
// ============================================================================

export const newsletterSubscribeSchema = z.object({
  email: z.string().email(),
  preferences: z.array(z.string()).optional(),
  source: z.string().default("footer"),
});

export const newsletterCampaignCreateSchema = z.object({
  subject: z.string().min(1).max(200),
  subjectSk: z.string().max(200).optional(),
  content: z.string().min(1),
  contentSk: z.string().optional(),
  aiGenerated: z.boolean().default(false),
  aiPrompt: z.string().optional(),
  scheduledAt: z.coerce.date().optional(),
});

// ============================================================================
// GIFT VOUCHER SCHEMAS
// ============================================================================

export const giftVoucherCreateSchema = z.object({
  amount: z.number().int().min(1000).max(500000),
  currency: z.string().length(3).default("CZK"),
  buyerName: z.string().min(1).max(200),
  buyerEmail: z.string().email(),
  recipientName: z.string().max(200).optional(),
  recipientEmail: z.string().email().optional(),
  personalMessage: z.string().max(500).optional(),
  designTemplate: z.string().default("default"),
  expiresAt: z.coerce.date(),
});

// ============================================================================
// QUIZ SCHEMA
// ============================================================================

export const quizSubmitSchema = z.object({
  answers: z.record(z.string()),
  email: z.string().email().optional(),
  subscribeNewsletter: z.boolean().default(false),
});

// ============================================================================
// AUTH SCHEMAS
// ============================================================================

export const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
});

export const userCreateSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
  name: z.string().min(1).max(200),
  role: z.enum(USER_ROLES).default("editor"),
});

// ============================================================================
// REVIEW SCHEMAS
// ============================================================================

export const reviewCreateSchema = z.object({
  tripId: z.string().optional(),
  destinationId: z.string().optional(),
  guideId: z.string().optional(),
  customerName: z.string().min(1).max(200),
  customerLocation: z.string().max(100).optional(),
  rating: z.number().int().min(1).max(5),
  title: z.string().max(200).optional(),
  content: z.string().min(10).max(2000),
  tripDate: z.coerce.date().optional(),
  isVideo: z.boolean().default(false),
  videoUrl: z.string().url().optional(),
  isFeatured: z.boolean().default(false),
  isApproved: z.boolean().default(false),
});

export const reviewUpdateSchema = reviewCreateSchema.partial();

// ============================================================================
// COMMUNICATION SCHEMAS
// ============================================================================

export const communicationCreateSchema = z.object({
  orderId: z.string().optional(),
  customerId: z.string().min(1),
  type: z.enum(COMMUNICATION_TYPES),
  direction: z.enum(COMMUNICATION_DIRECTIONS),
  subject: z.string().max(500).optional(),
  content: z.string().optional(),
  attachments: z.array(z.string().url()).optional(),
});

// ============================================================================
// TYPE EXPORTS
// ============================================================================

export type PaginationInput = z.infer<typeof paginationSchema>;
export type InquiryInput = z.infer<typeof inquirySchema>;
export type TripFilterInput = z.infer<typeof tripFilterSchema>;
export type NewsletterSubscribeInput = z.infer<typeof newsletterSubscribeSchema>;
export type QuizSubmitInput = z.infer<typeof quizSubmitSchema>;
export type LoginInput = z.infer<typeof loginSchema>;
export type DestinationCreateInput = z.infer<typeof destinationCreateSchema>;
export type GuideCreateInput = z.infer<typeof guideCreateSchema>;
export type TripCreateInput = z.infer<typeof tripCreateSchema>;
export type TripDateCreateInput = z.infer<typeof tripDateCreateSchema>;
export type CustomerCreateInput = z.infer<typeof customerCreateSchema>;
export type OrderCreateInput = z.infer<typeof orderCreateSchema>;
export type ReviewCreateInput = z.infer<typeof reviewCreateSchema>;
export type GiftVoucherCreateInput = z.infer<typeof giftVoucherCreateSchema>;

// Aliases for backward compatibility
export const destinationSchema = destinationCreateSchema;
export const guideSchema = guideCreateSchema;
export const tripSchema = tripCreateSchema;
export const tripDateSchema = tripDateCreateSchema;
export const customerSchema = customerCreateSchema;
export const orderSchema = orderCreateSchema;
export const reviewSchema = reviewCreateSchema;

// Type aliases for backward compatibility
export type DestinationInput = DestinationCreateInput;
export type GuideInput = GuideCreateInput;
export type TripInput = TripCreateInput;
export type TripDateInput = TripDateCreateInput;
export type CustomerInput = CustomerCreateInput;
export type OrderInput = OrderCreateInput;
export type ReviewInput = ReviewCreateInput;
